<template>
  <v-container class="driver-card">
    <v-layout>
      <v-flex xs12 pt-4 pb-0>
        <h3>Водитель {{ driver.n }}</h3>
      </v-flex>
    </v-layout>

    <!-- ── Водитель из бэкенда: редактирование фамилии и ВУ ─────────────── -->
    <template v-if="driver.isFromBack">
      <!-- Текущие данные (read-only) -->
      <v-layout row wrap align-end>
        <v-flex xs12 sm6 md6 lg4 py-0>
          <osago-name :value="driver.lastname" label="Фамилия" :isDisabled="true" />
        </v-flex>
        <v-flex xs12 sm6 md6 lg4 py-0>
          <osago-name :value="driver.firstname" label="Имя" :isDisabled="true" />
        </v-flex>
        <v-flex xs12 sm6 md6 lg4 py-0>
          <osago-name
            :value="driver.middlename"
            label="Отчество"
            optional="true"
            genitiveLabel="отчества"
            :isDisabled="true"
          />
        </v-flex>
      </v-layout>

      <!-- Новая фамилия -->
      <v-layout row wrap align-center>
        <v-flex xs12 sm6 md6 lg4 py-0>
          <osago-name
            label="Новая фамилия"
            :value="driver.lastnameNew"
            :rules="[rules.differentFromOld(driver.lastname)]"
            :isRequired="false"
            @input="$emit('update:lastnameNew', { driver, value: $event })"
          />
        </v-flex>
        <v-flex>
          <p class="hint-text">При изменении фамилии впишите новую</p>
        </v-flex>
      </v-layout>

      <!-- Текущее ВУ (read-only) -->
      <v-layout row wrap align-end>
        <v-flex xs12 sm6 md6 lg4 py-0>
          <v-text-field
            :value="driver.license.seriesNumber"
            label="Права: серия и номер"
            disabled
          />
        </v-flex>
      </v-layout>

      <!-- Новое ВУ -->
      <v-layout row wrap align-center>
        <v-flex xs12 sm6 md6 lg4 py-0>
          <v-text-field
            :value="driver.license.seriesNumberNew"
            label="Новые права: серия и номер"
            :rules="[rules.license, rules.differentFromOld(driver.license.seriesNumber)]"
            :mask="driver.licenseMask || 'S'"
            return-masked-value
            validate-on-blur
            @paste="$emit('paste-license', { event: $event, driver })"
            @input="$emit('update:licenseNew', { driver, value: $event })"
          />
        </v-flex>
        <v-flex>
          <p class="hint-text">При изменении водительского удостоверения впишите новое</p>
        </v-flex>
      </v-layout>
    </template>

    <!-- ── Новый водитель: полная форма ─────────────────────────────────── -->
    <template v-else>
      <v-layout row wrap align-end>
        <v-flex xs12 sm6 md6 lg3 py-0>
          <osago-name
            label="Фамилия"
            :value="driver.lastname"
            @input="$emit('update:field', { driver, field: 'lastname', value: $event })"
          />
        </v-flex>
        <v-flex xs12 sm6 md6 lg3 py-0>
          <osago-name
            label="Имя"
            :value="driver.firstname"
            @input="$emit('update:field', { driver, field: 'firstname', value: $event })"
          />
        </v-flex>
        <v-flex xs12 sm6 md6 lg3 py-0>
          <osago-name
            label="Отчество"
            :value="driver.middlename"
            optional="true"
            genitiveLabel="отчества"
            :noField.sync="driver.noMiddlename"
            @input="$emit('update:field', { driver, field: 'middlename', value: $event })"
          />
          <osago-gender
            v-if="driver.noMiddlename"
            :value="driver.gender"
            label="Пол"
            @input="$emit('update:field', { driver, field: 'gender', value: $event })"
          />
        </v-flex>
        <v-flex xs12 sm6 md6 lg3 py-0>
          <osago-date
            :value="driver.birthdate"
            label="Дата рождения"
            isBirthday="true"
            :max="dateDriversMinimal"
            :isHideMobile="true"
            @change="$emit('birthdate-change', { driver, value: $event })"
          />
        </v-flex>
      </v-layout>

      <v-layout row wrap align-end>
        <v-flex xs12 sm6 md6 lg3 py-0>
          <v-text-field
            :value="driver.license.seriesNumber"
            label="Права: серия и номер"
            :rules="[rules.license]"
            :mask="driver.licenseMask || 'S'"
            return-masked-value
            validate-on-blur
            @paste="$emit('paste-license', { event: $event, driver })"
            @input="$emit('update:license', { driver, value: $event })"
          />
        </v-flex>
        <v-flex xs12 sm6 md6 lg3 py-0>
          <osago-date
            :value="driver.license.issueDate"
            label="Дата выдачи первых прав"
            :isFirstLicense="true"
            :min="driver.dateFirstLicense"
            :max="dateToday"
            :restrictJan1="true"
            :isHideMobile="true"
            @input="$emit('update:field', { driver, field: 'license.issueDate', value: $event })"
          />
        </v-flex>
      </v-layout>
    </template>

    <!-- ── Кнопки управления ─────────────────────────────────────────────── -->
    <v-layout row wrap>
      <v-flex
        v-if="canAddDriver"
        shrink pt-0
        class="driver-card__controls"
      >
        <a @click="$emit('add-driver')" class="driver-card__action-link">
          <img src="/svg/plus.svg" class="driver-card__icon" alt="+" />
          Добавить водителя
        </a>
      </v-flex>

      <v-flex
        v-if="!driver.isOwner && !driver.isInsurer"
        grow pt-0
        class="driver-card__controls"
      >
        <a @click="$emit('remove-driver', driver)" class="driver-card__action-link">
          <img src="/svg/minus.svg" class="driver-card__icon" alt="-" />
          Убрать водителя
        </a>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { useOsagoValidationRules } from '../composables/useOsagoValidationRules'

/**
 * DriverCard — "тупой" компонент карточки водителя.
 *
 * Получает данные через props, сообщает об изменениях через emit.
 * Не мутирует props напрямую.
 *
 * Emits:
 *   add-driver                                    — добавить нового водителя
 *   remove-driver(driver)                         — удалить водителя
 *   paste-license({ event, driver })              — вставка в поле ВУ
 *   update:licenseNew({ driver, value })          — новое ВУ (isFromBack)
 *   update:license({ driver, value })             — ВУ нового водителя
 *   update:lastnameNew({ driver, value })         — новая фамилия (isFromBack)
 *   update:field({ driver, field, value })        — любое другое поле
 *   birthdate-change({ driver, value })           — изменение даты рождения
 */
export default {
  name: 'DriverCard',

  props: {
    driver: {
      type: Object,
      required: true,
    },
    /** Показывать ли кнопку "Добавить водителя" */
    canAddDriver: {
      type: Boolean,
      default: false,
    },
    /** Минимальная дата (18 лет назад) */
    dateDriversMinimal: {
      type: String,
      required: true,
    },
    /** Сегодняшняя дата */
    dateToday: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      rules: {
        license: (v) => {
          if (!v) return true
          return (
            /^\d{2}[\dА-ЯA-Z]{2} \d{6}$/.test(v) ||
            /^[A-Za-zА-Яа-яЁё]{2} \d{6}$/.test(v) ||
            'Неверный формат'
          )
        },
        differentFromOld: (oldValue) => (v) => {
          if (!v) return true
          if (v === oldValue) return 'Новое значение не должно совпадать со старым'
          return true
        },
      }
    }
  }
}
</script>

<style scoped lang="scss">
.hint-text {
  margin-bottom: 0;
}

.driver-card {
  max-width: 1240px;
  padding: 0 30px 50px;
  border-bottom: 1px solid #cecece;

  &__controls {
    padding-top: 0;
  }

  &__action-link {
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    text-decoration: none;
    color: inherit;
  }

  &__icon {
    width: 21px;
    height: 21px;
  }
}
</style>



// WEBPACK FOOTER //
// resources/js/components/change/components/DriverCard.vue